import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { useRenders } from '../hooks/api/useRenders';
import { apiUrl } from '../config/apiBase';

function formatDate(value, locale) {
  if (!value) return '—';
  return new Date(value).toLocaleString(locale);
}

function formatDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes} min ${remainingSeconds} s`;
}

function formatCostUsd(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function getStatusColor(status) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'info';
    default:
      return 'default';
  }
}

function modelLabel(model, t) {
  if (!model) return '—';
  const key = `renders.model.${model}`;
  const translated = t(key);
  return translated === key ? model : translated;
}

export function RendersPage() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [previewRender, setPreviewRender] = useState(null);
  const { data, isLoading, error } = useRenders({ q: search, limit: 100 });

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const previewUrl =
    (previewRender?.resultImageUrl ?? previewRender?.sourceImageUrl)
      ? apiUrl(previewRender.resultImageUrl ?? previewRender.sourceImageUrl)
      : null;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('renders.title')}
      </Typography>

      <TextField
        size="small"
        label={t('renders.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        fullWidth
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error.message || t('common.error')}</Alert>}

      {!isLoading && !error && (
        <Paper>
          {items.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('renders.empty')}</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('renders.createdAt')}</TableCell>
                    <TableCell>{t('renders.user')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {t('renders.organization')}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('renders.project')}</TableCell>
                    <TableCell>{t('renders.model')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('renders.duration')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('renders.cost')}</TableCell>
                    <TableCell>{t('renders.status')}</TableCell>
                    <TableCell align="right">{t('renders.image')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((render) => (
                    <TableRow key={render.id} hover>
                      <TableCell>{formatDate(render.createdAt, i18n.language)}</TableCell>
                      <TableCell>{render.user?.email ?? '—'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {render.organization ? (
                          <Link component={RouterLink} to={`/organizations/${render.organization.uuid}`}>
                            {render.organization.name}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {render.project?.name ?? '—'}
                      </TableCell>
                      <TableCell>{modelLabel(render.model, t)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {formatDuration(render.durationMs)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCostUsd(render.costUsdCents)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={getStatusColor(render.status)}
                          label={t(`renders.status-${render.status}`, render.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<ImageIcon />}
                          disabled={!render.resultImageUrl && !render.sourceImageUrl}
                          onClick={() => setPreviewRender(render)}
                        >
                          {t('renders.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={Boolean(previewRender)} onClose={() => setPreviewRender(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('renders.preview-title')}</DialogTitle>
        <DialogContent>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt={t('renders.preview-alt')}
              sx={{ display: 'block', maxWidth: '100%', borderRadius: 1 }}
            />
          ) : (
            <Typography color="text.secondary">{t('renders.preview-unavailable')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {previewUrl && (
            <Button component="a" href={previewUrl} target="_blank" rel="noopener noreferrer">
              {t('renders.open-image')}
            </Button>
          )}
          <Button onClick={() => setPreviewRender(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
